import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  getActiveCheckinSession,
  SALON_CHECKIN_UPDATED_EVENT,
} from '../salon/utils/salonFaceSession';
import {
  finalizeOpenVisitOnLogout,
  resolveAndRestoreActiveVisit,
} from '../salon/utils/visitHandoff';
import './FranchiseFlowSessionBar.css';
import '../salon/components/SalonHeaderActions.css';

function LogoutIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

/**
 * Floating logout icon for Face Identity / Salon franchise device sessions.
 * Confirms sign-out and clears local visit handoff (does not mark visits completed).
 */
export default function FranchiseFlowSessionBar({
  busy = false,
  onLogout,
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [openVisit, setOpenVisit] = useState(() => getActiveCheckinSession());

  useEffect(() => {
    const sync = () => setOpenVisit(getActiveCheckinSession());
    sync();
    window.addEventListener(SALON_CHECKIN_UPDATED_EVENT, sync);
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener(SALON_CHECKIN_UPDATED_EVENT, sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  const hasOpenVisit =
    openVisit?.id &&
    openVisit.status !== 'completed' &&
    openVisit.status !== 'cancelled';

  const handleOpenConfirm = async () => {
    if (busy || submitting) return;
    setError('');
    try {
      const restored = await resolveAndRestoreActiveVisit();
      if (restored) setOpenVisit(restored);
      else setOpenVisit(getActiveCheckinSession());
    } catch {
      setOpenVisit(getActiveCheckinSession());
    }
    setConfirmOpen(true);
  };

  const handleConfirmLogout = async () => {
    if (busy || submitting || typeof onLogout !== 'function') return;
    setSubmitting(true);
    setError('');
    try {
      await finalizeOpenVisitOnLogout({ complete: false });
      setConfirmOpen(false);
      await onLogout();
    } catch (err) {
      setError(err?.message || 'Could not sign out. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="ff-session-bar" role="region" aria-label="Franchise session">
        <button
          type="button"
          className="ff-session-bar__logout-icon"
          onClick={handleOpenConfirm}
          disabled={busy || submitting}
          aria-label={busy || submitting ? 'Signing out' : 'Sign out'}
          title="Sign out"
        >
          <LogoutIcon />
        </button>
      </div>

      {confirmOpen
        ? createPortal(
            <div
              className="salon-logout-modal-overlay"
              role="presentation"
              onClick={() => {
                if (!submitting) setConfirmOpen(false);
              }}
            >
              <div
                className="salon-logout-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ff-logout-confirm-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="salon-logout-modal-icon" aria-hidden="true">
                  <LogoutIcon />
                </div>
                <h2 id="ff-logout-confirm-title" className="salon-logout-modal-title">
                  Sign out of this kiosk?
                </h2>
                <p className="salon-logout-modal-description">
                  {hasOpenVisit
                    ? 'A guest visit is still open. Signing out clears this device only — finish the visit from Service session or the partner check-in list.'
                    : 'This ends the franchise device session. You will need to sign in again to use Face Identity or Salon.'}
                </p>
                {error ? <p className="salon-logout-modal-error">{error}</p> : null}
                <div className="salon-logout-modal-actions">
                  <button
                    type="button"
                    className="salon-logout-modal-button salon-logout-modal-button-secondary"
                    disabled={submitting}
                    onClick={() => setConfirmOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="salon-logout-modal-button salon-logout-modal-button-primary"
                    disabled={submitting}
                    onClick={handleConfirmLogout}
                  >
                    {submitting ? 'Signing out…' : 'Confirm sign out'}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
